import React, { useState, useEffect } from 'react';
import { Card, CardTitle, CardBody, Button, Input, Table } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { apiService } from '../../constants/ApiService';
import useVendorBranch from '../../utils/useVendorBranch';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { formatDate } from '../../constants/DateFormatter';

const CatalogList = () => {
  const { user } = useSelector((state) => state.authState);
  const navigate = useNavigate();

  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [catalogList, setCatalogList] = useState([]);
  const [search, setSearch] = useState('');
  const [errorMessage, setErrorMessage] = useState({});

  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });

  // -----------------------------
  // NAVIGATION
  // -----------------------------
  const onHandleAdd = () => navigate('/save-catalog');

  const onHandleEdit = (id) => {
    navigate('/save-catalog', {
      state: {
        catalog_id: id,
        vendor_id: selectionData.vendor_id || '',
        branch_id: selectionData.branch_id || '',
      },
    });
  };

  const onHandleView = (id) => {
    navigate('/catalog-render', {
      state: {
        catalog_id: id,
        vendor_id: selectionData.vendor_id || '',
        branch_id: selectionData.branch_id || '',
      },
    });
  };
  // -----------------------------
  // GET LIST
  // -----------------------------
  const GetCatalogList = async (searchText = '') => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      const res = await apiService.get(
        `api/Catalog/GetAllCatalogs?vendorId=${vendorId}&branchId=${branchId}&search=${encodeURIComponent(
          searchText
        )}`
      );

      if (res?.status === true) {
        setCatalogList(res.data || []);
      } else {
        toast.error(res?.message || 'Failed to load catalogs');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong');
    }
  };

  // -----------------------------
  // DELETE (FIXED ✅)
  // -----------------------------
  const onHandleDelete = async (row) => {
    const catalogId = row?.catalog_id;
    const vendor_id = row?.vendor_id;
    const branch_id = row?.branch_id;

    if (!catalogId) return;

    if (!window.confirm('Delete this record?')) return;

    try {
      const res = await apiService.delete(
        `api/Catalog/DeleteCatalog?catalogId=${catalogId}&vendorId=${vendor_id}&branchId=${branch_id}`
      );

      if (res?.status === true) {
        toast.success(res.message || 'Deleted successfully');

        // refresh list
        GetCatalogList(search);
      } else {
        toast.error(res?.message || 'Failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong');
    }
  };

  // -----------------------------
  // SEARCH
  // -----------------------------
  const onHandleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    GetCatalogList(val);
  };

  // -----------------------------
  // LOAD ON VENDOR / BRANCH CHANGE
  // -----------------------------
  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });

    if (canLoad) {
      GetCatalogList(search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionData.vendor_id, selectionData.branch_id]);

  // -----------------------------
  // UI
  // -----------------------------
  return (
    <>
      <div className='text-right mb-2'>
        <Button color='primary' onClick={onHandleAdd}>
          <i className='bi bi-plus-circle' /> Add Catalog
        </Button>
      </div>

      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton
      />

      <Card className='card-box pd-20 mb-20'>
        <CardTitle className='p-3 mb-0'>
          <i className='bi bi-journal-text mr-2' /> Catalog List
        </CardTitle>

        <div className='p-2'>
          <Input
            placeholder='Search Catalog...'
            value={search}
            onChange={onHandleSearch}
          />
        </div>

        <CardBody>
          <Table striped hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Edit</th>
                <th>View</th>
                <th>Delete</th>
                <th>Catalog Name</th>
                <th>Year</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {catalogList.length > 0 ? (
                catalogList.map((x, i) => (
                  <tr key={x.catalog_id || i}>
                    <td>{i + 1}</td>

                    {/* EDIT */}
                    <td>
                      <i
                        className='bi bi-pencil text-primary'
                        style={{ cursor: 'pointer' }}
                        onClick={() => onHandleEdit(x.catalog_id)}
                      />
                    </td>
                    <td>
                      <i
                        className='bi bi-eye-fill text-success'
                        style={{ cursor: 'pointer', fontSize: '18px' }}
                        onClick={() => onHandleView(x)}
                        title='View'
                      />
                    </td>

                    {/* DELETE */}
                    <td>
                      <i
                        className='bi bi-trash text-danger'
                        style={{ cursor: 'pointer' }}
                        onClick={() => onHandleDelete(x)}
                      />
                    </td>

                    <td>{x.catalog_name}</td>
                    <td>{x.catalog_year}</td>
                    <td>{x.catalog_date ? formatDate(x.catalog_date) : ''}</td>
                    <td>{x.is_active ? 'Active' : 'Inactive'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan='7' className='text-center'>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </>
  );
};

export default CatalogList;
